import { TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';

import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';

import {
  formatearCoordenada,
  fueraDeGuatemala,
  leerCoordenada,
  leerParCoordenadas,
} from './coordenadas-manuales';

import type { LatLng } from './location-picker-map';

export interface CoordenadasInputsProps {
  value: LatLng | null;
  onChange: (value: LatLng | null) => void;
  /** El usuario terminó de escribir un punto válido (blur, Enter o pegado): recentrar el mapa. */
  onConfirmar: (value: LatLng) => void;
  disabled?: boolean;
}

type Eje = 'lat' | 'lng';

/**
 * Latitud y longitud escritas a mano, sincronizadas con el mapa en ambos
 * sentidos: un punto válido mueve el pin y un clic o arrastre en el mapa
 * reescribe los campos. Mientras el par esté incompleto o sea inválido el
 * valor es `null`, así el formulario no envía un punto distinto al que se ve.
 */
export function CoordenadasInputs({
  value,
  onChange,
  onConfirmar,
  disabled = false,
}: CoordenadasInputsProps) {
  const id = useId();
  const [texto, setTexto] = useState<Record<Eje, string>>(() => textosDe(value));
  const [tocado, setTocado] = useState<Record<Eje, boolean>>({ lat: false, lng: false });
  // Último valor que emitieron estos campos: si `value` vuelve igual es eco de
  // lo escrito y no se reescribe el texto (se perdería p. ej. «14.» a medias).
  const emitido = useRef<LatLng | null>(value);

  useEffect(() => {
    if (mismoPunto(value, emitido.current)) return;
    emitido.current = value;
    setTexto(textosDe(value));
    setTocado({ lat: false, lng: false });
  }, [value]);

  const emitir = (next: LatLng | null) => {
    if (next === null && value === null) return;
    emitido.current = next;
    onChange(next);
  };

  const parValido = (t: Record<Eje, string>): LatLng | null => {
    const lat = leerCoordenada(t.lat, 'lat');
    const lng = leerCoordenada(t.lng, 'lng');
    return lat.ok && lng.ok ? { lat: lat.valor, lng: lng.valor } : null;
  };

  const cambiar = (eje: Eje, nuevo: string) => {
    const t = { ...texto, [eje]: nuevo };
    setTexto(t);
    emitir(parValido(t));
  };

  const confirmar = () => {
    const par = parValido(texto);
    if (par) onConfirmar(par);
  };

  const alSalir = (eje: Eje) => {
    setTocado((prev) => ({ ...prev, [eje]: true }));
    confirmar();
  };

  const alPresionarTecla = (e: KeyboardEvent<HTMLInputElement>) => {
    // Enter no debe enviar el formulario del diálogo: solo fija el punto.
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmar();
    }
  };

  // «14.634915, -90.506882» pegado en cualquiera de los dos campos llena ambos.
  const alPegar = (e: ClipboardEvent<HTMLInputElement>) => {
    const par = leerParCoordenadas(e.clipboardData.getData('text'));
    if (!par) return;
    e.preventDefault();
    setTexto(textosDe(par));
    setTocado({ lat: true, lng: true });
    emitir(par);
    onConfirmar(par);
  };

  // Errores tras salir del campo. Vacío solo es error si el otro eje ya tiene
  // texto (un par a medias); ambos vacíos es «sin ubicación».
  const errorDe = (eje: Eje): string | undefined => {
    if (!tocado[eje]) return undefined;
    const otro: Eje = eje === 'lat' ? 'lng' : 'lat';
    if (texto[eje].trim() === '' && texto[otro].trim() === '') return undefined;
    const r = leerCoordenada(texto[eje], eje);
    return r.ok ? undefined : r.error;
  };

  const campos: Array<{ eje: Eje; etiqueta: string; ejemplo: string }> = [
    { eje: 'lat', etiqueta: 'Latitud', ejemplo: '14.634915' },
    { eje: 'lng', etiqueta: 'Longitud', ejemplo: '-90.506882' },
  ];

  const puntoActual = parValido(texto);
  const avisoPais = puntoActual != null && fueraDeGuatemala(puntoActual);

  return (
    <fieldset className="space-y-1.5" disabled={disabled}>
      <legend className="sr-only">Coordenadas</legend>
      <div className="grid grid-cols-2 gap-2">
        {campos.map(({ eje, etiqueta, ejemplo }) => {
          const error = errorDe(eje);
          const inputId = `${id}-${eje}`;
          const errorId = `${inputId}-error`;
          return (
            <div key={eje} className="min-w-0 space-y-1">
              <Label htmlFor={inputId} className="text-xs">
                {etiqueta}
              </Label>
              <Input
                id={inputId}
                value={texto[eje]}
                onChange={(e) => cambiar(eje, e.target.value)}
                onBlur={() => alSalir(eje)}
                onKeyDown={alPresionarTecla}
                onPaste={alPegar}
                placeholder={ejemplo}
                autoComplete="off"
                spellCheck={false}
                // Sin `inputMode="decimal"`: en iOS ese teclado no trae el
                // signo menos y la longitud de Guatemala es negativa.
                className="tabular-nums aria-[invalid=true]:border-destructive"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
              />
              {error ? (
                <p id={errorId} className="text-[11px] text-destructive">
                  {error}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
      {avisoPais ? (
        <p className="flex items-start gap-1.5 text-[11px] text-warning" aria-live="polite">
          <TriangleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          El punto queda fuera de Guatemala. Revise los signos: en Guatemala la longitud es negativa
          (oeste).
        </p>
      ) : null}
    </fieldset>
  );
}

function mismoPunto(a: LatLng | null, b: LatLng | null): boolean {
  if (a === null || b === null) return a === b;
  return a.lat === b.lat && a.lng === b.lng;
}

function textosDe(value: LatLng | null): Record<Eje, string> {
  return value
    ? { lat: formatearCoordenada(value.lat), lng: formatearCoordenada(value.lng) }
    : { lat: '', lng: '' };
}
